import React from 'react';
import { Link } from 'react-router-dom';
import { Header, SEOHead } from '../components';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { 
  ChartBarIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  PresentationChartLineIcon,
  ArrowRightIcon,
  CheckIcon,
  StarIcon,
  PhoneIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

const DigitalMarketingPage: React.FC = () => {
  // SEO setup
  useSEO(pageConfigs.digitalMarketing);
  const features = [
    {
      title: "Search Engine Optimization",
      description: "Improve your website's visibility in search engines with our data-driven SEO strategies.",
      icon: <ChartBarIcon className="w-8 h-8" />,
      benefits: ["Keyword research & strategy", "On-page optimization", "Technical SEO", "Link building"]
    },
    {
      title: "Content Marketing",
      description: "Create valuable content that attracts and engages your target audience while establishing authority.",
      icon: <DocumentTextIcon className="w-8 h-8" />,
      benefits: ["Content strategy", "Blog management", "Infographics & visuals", "Content distribution"]
    },
    {
      title: "Email Marketing",
      description: "Build relationships with customers through personalized email campaigns that drive conversions.",
      icon: <EnvelopeIcon className="w-8 h-8" />,
      benefits: ["Campaign strategy", "List segmentation", "Automation workflows", "Performance analytics"]
    },
    {
      title: "Analytics & Reporting",
      description: "Make data-driven decisions with comprehensive analytics and custom reporting dashboards.",
      icon: <PresentationChartLineIcon className="w-8 h-8" />,
      benefits: ["Custom dashboards", "Conversion tracking", "User behavior analysis", "ROI reporting"]
    }
  ];

  const process = [
    {
      step: "01",
      title: "Discovery & Analysis",
      description: "We analyze your business, competitors, and target audience to identify the best digital marketing opportunities."
    },
    {
      step: "02",
      title: "Strategy Development",
      description: "Create a comprehensive digital marketing strategy tailored to your business goals and market position."
    },
    {
      step: "03",
      title: "Implementation",
      description: "Execute the strategy across all relevant digital channels with precision and attention to detail."
    },
    {
      step: "04",
      title: "Optimization & Growth",
      description: "Continuously monitor performance and optimize campaigns to maximize results and ROI."
    }
  ];

  const caseStudy = {
    client: "E-commerce Retailer",
    industry: "Retail",
    challenge: "Struggling to increase online sales and compete with larger retailers",
    solution: "Implemented comprehensive digital marketing strategy including SEO, content marketing, and email campaigns",
    results: [
      "250% revenue increase in 12 months",
      "185% increase in organic traffic",
      "320% growth in email marketing revenue",
      "ROI of 380% on digital marketing spend"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.digitalMarketing} />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800"></div>
        
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-blue-500/20 px-4 py-2 rounded-full border border-blue-400/50 mb-8">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <span className="text-blue-300 font-semibold text-sm">Data-Driven Strategies</span>
              </div>
              
              <h1 className="heading-section text-white mb-6">
                Comprehensive 
                <span className="bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent"> Digital Marketing</span>
              </h1>
              
              <p className="text-body-lg text-slate-300 mb-8 max-w-2xl">
                Strategic digital marketing solutions that drive measurable results. 
                Our data-driven approach ensures maximum ROI and sustainable growth.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link 
                  to="/consultation"
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl hover:scale-105 transform"
                >
                  <span>Get Digital Marketing Audit</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>
                <Link 
                  to="/portfolio"
                  className="bg-white/10 hover:bg-white/20 text-white font-semibold py-4 px-8 rounded-lg border-2 border-white/30 transition-all duration-300 text-center backdrop-blur-sm hover:scale-105 transform"
                >
                  View Case Studies
                </Link>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-300 mb-1">250%</div>
                  <div className="text-sm font-semibold text-slate-300 mb-1">Revenue Growth</div>
                  <div className="text-xs text-slate-400">Average for clients</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-300 mb-1">185%</div>
                  <div className="text-sm font-semibold text-slate-300 mb-1">Traffic Increase</div>
                  <div className="text-xs text-slate-400">Organic search</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-300 mb-1">320%</div>
                  <div className="text-sm font-semibold text-slate-300 mb-1">Email Revenue</div>
                  <div className="text-xs text-slate-400">Growth</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-300 mb-1">380%</div>
                  <div className="text-sm font-semibold text-slate-300 mb-1">ROI</div>
                  <div className="text-xs text-slate-400">Average return</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-3xl blur-2xl"></div>
              <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/20">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                    <ChartBarIcon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-display font-bold text-slate-900">Free Digital Marketing Audit</h3>
                </div>
                <p className="text-slate-600 mb-6">
                  Get a comprehensive analysis of your current digital marketing performance and 
                  receive a customized strategy to improve your results.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-slate-700">SEO performance analysis</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-slate-700">Content strategy review</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-slate-700">Custom marketing recommendations</span>
                  </div>
                </div>
                
                <Link 
                  to="/consultation"
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 text-center block shadow-lg hover:shadow-xl"
                >
                  Get Your Free Audit
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-gradient-to-b from-slate-50 to-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="heading-section text-slate-900 mb-4">
              Comprehensive Digital Marketing Solutions
            </h2>
            <p className="text-body-lg text-slate-600 max-w-3xl mx-auto">
              Our multi-channel approach ensures your business maximizes its digital presence and ROI
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => {
              const gradients = [
                'from-blue-500 to-blue-600',
                'from-green-500 to-emerald-600',
                'from-purple-500 to-pink-600',
                'from-orange-500 to-amber-600'
              ];
              return (
                <div key={index} className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-100 hover:border-slate-200 hover:scale-105 transform">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className={`bg-gradient-to-br ${gradients[index % gradients.length]} p-3 rounded-xl group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <div className="text-white">{feature.icon}</div>
                    </div>
                    <div className="flex-1">
                      <h3 className="heading-card text-slate-900 mb-2">{feature.title}</h3>
                      <p className="text-slate-600">{feature.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {feature.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm text-slate-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="heading-section text-slate-900 mb-4">
              Our 4-Step Process
            </h2>
            <p className="text-body-lg text-slate-600">
              Proven methodology for digital marketing success
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((step, index) => (
              <div key={index} className="group text-center p-6 rounded-xl hover:bg-slate-50 transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <span className="text-white font-bold text-xl">{step.step}</span>
                </div>
                <h3 className="heading-card text-slate-900 mb-4">{step.title}</h3>
                <p className="text-slate-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section className="section-padding bg-gradient-to-b from-slate-50 to-white">
        <div className="container-custom">
          <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 rounded-3xl p-8 lg:p-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center relative z-10">
              <div>
                <div className="inline-flex items-center space-x-2 bg-green-500/20 px-4 py-2 rounded-full border border-green-400/50 mb-8">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-300 font-semibold text-sm">Success Story</span>
                </div>
                
                <h2 className="heading-section text-white mb-6">
                  {caseStudy.client}
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-blue-300 mb-2">Challenge</h4>
                    <p className="text-slate-300">{caseStudy.challenge}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-300 mb-2">Solution</h4>
                    <p className="text-slate-300">{caseStudy.solution}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-300 mb-2">Results</h4>
                    <div className="space-y-3">
                      {caseStudy.results.map((result, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                          <span className="text-slate-300">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-3xl blur-2xl"></div>
                <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/20">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <span className="text-2xl">📈</span>
                    </div>
                    <h3 className="heading-card text-slate-900 mb-2">Ready to grow your business?</h3>
                    <p className="text-slate-600">Schedule a consultation with our digital marketing experts</p>
                  </div>
                  
                  <Link 
                    to="/consultation"
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 mb-4 text-center block shadow-lg hover:shadow-xl"
                  >
                    Schedule Consultation
                  </Link>
                  
                  <Link 
                    to="/portfolio"
                    className="w-full bg-white hover:bg-gray-50 text-slate-900 font-semibold py-3 px-6 rounded-lg border-2 border-slate-200 transition-all duration-300 text-center block"
                  >
                    View More Case Studies
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800"></div>
        
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
        
        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="heading-section text-white mb-6">
              Ready to Transform Your Digital Marketing?
            </h2>
            <p className="text-body-lg text-slate-300 mb-8">
              Get started with a free digital marketing audit and custom strategy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/quote"
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 text-center shadow-lg hover:shadow-xl hover:scale-105 transform"
              >
                Get Started
              </Link>
              <Link 
                to="/portfolio"
                className="bg-white/10 hover:bg-white/20 text-white font-semibold py-4 px-8 rounded-lg border-2 border-white/30 transition-all duration-300 text-center backdrop-blur-sm hover:scale-105 transform"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default DigitalMarketingPage;